
import { GIFT_TYPE } from "@olga-food/schemas/lib/classes/schemas/order/gift/Gift";
import { OMath } from "@olga-food/schemas/lib/classes/tools/OMath";
import { v4 as uuid } from "uuid";
import UUIDEncoder from "uuid-encoder";
import { NetLogicCombo } from "./catalog/NetLogicCombo";
import { NetLogicItem } from "./catalog/NetLogicItem";
import { NetLogicPayment } from "./NetLogicPayment";

const convertIntToUUID = (number) => {
  const e = new UUIDEncoder("base10");
  return e.encode(`${number}`);
};

const removeEmptyProps = (obj) => {
  return obj;
};

export class NetLogicOrder {
  check_number = null;

  constructor({ checkSeq, checkNum, checkId, tableNum, type, revenueCenter, employeeObjectNum, tenderMedia, ...order }) {
    this.order = order || {};
    this.checkSeq = checkSeq;
    this.checkNum = checkNum;
    this.checkId = checkId;
    this.tableNum = tableNum;
    this.type = type;
    this.revenueCenter = revenueCenter;
    this.employeeObjectNum = employeeObjectNum;
    this.tenderMedia = tenderMedia;
  }

  get id() {
    return this.order.uuid || uuid();
  }

  get reference() {
    return this.order.uuid || uuid();
  }

  get shortReference() {
    return this.order.uuid || uuid();
  }

  get createdAt() {
    return this.order.created_at || new Date();
  }

  get scheduled() {
    return false;
  }

  get discount() {
    const { gift } = this.order;
    const exists = gift && gift.value;
    const is_cashback_earn =
      (gift?.type & GIFT_TYPE.CASHBACK_EARN) >= GIFT_TYPE.CASHBACK_EARN;

    if (exists && !is_cashback_earn) {
      const isCartType = (gift.type & GIFT_TYPE.CART) >= GIFT_TYPE.CART;
      const isPercentage = (gift.type & GIFT_TYPE.PERCENT) >= GIFT_TYPE.PERCENT;

      const baseValue = isCartType
        ? this.order.cart.total
        : this.order.delivery.total;
      return +(isPercentage
        ? +OMath.times(baseValue, gift.value, 0.01)
        : gift.value);
    }

    return 0;
  }

  get discountCode() {
    const { gift } = this.order;

    const exists = gift && gift.value;
    const is_cashback_earn =
      (gift?.type & GIFT_TYPE.CASHBACK_EARN) >= GIFT_TYPE.CASHBACK_EARN;

    if (!exists || is_cashback_earn) {
      return 0;
    }

    return 0;
  }

  get discountInfo() {
    const { gift } = this.order;

    const exists = gift && gift?.value;
    const is_cashback_earn =
      (gift?.type & GIFT_TYPE.CASHBACK_EARN) >= GIFT_TYPE.CASHBACK_EARN;
    const is_cashback_use =
      (gift?.type & GIFT_TYPE.CASHBACK_USE) >= GIFT_TYPE.CASHBACK_USE;

    if (!exists || is_cashback_earn) {
      return "";
    }

    if (is_cashback_use) {
      return "CASHBACK";
    }

    return gift.code;
  }

  get merchant() {
    return {};
  }

  get benefits() {
    return [];
  }

  get payments() {
    const payment = new NetLogicPayment({
      ...this.order,
      tenderMedia: this.tenderMedia
    });
    return payment.export();
  }

  get customer() {
    const { order } = this;

    const customer = order?.customer || {};
    if (Object.values(customer).length === 0) {
      return null;
    }

    return {
      id: customer?.id || "",
      uuid: customer?.id ? convertIntToUUID(`${customer.id}`) : "",
      name: customer?.name || "",
      phone: customer?.phone || "",
      ordersCountOnRestaurant: "1",
      documentNumber: customer?.extra?.doc || "",
      taxPayerIdentificationNumber: customer?.extra?.doc || "",
    };
  }

  get items() {
    const items = this.order?.cart?.catalog?.items || [];
    const netlogic_items = [];

    for (const item of items) {
      if (NetLogicCombo.isCombo(item)) {
        continue;
      }

      const netlogic_item = new NetLogicItem(item);
      const converted_item = netlogic_item.export();

      const is_empty_item = (converted_item?.length || 0) === 0;
      const is_invalid_item = converted_item.some((subitem) => !subitem);

      if (is_empty_item || is_invalid_item) {
        console.log(`[NetLogic / OrderItems ]: Item inválido`, item);
      }

      netlogic_items.push(...converted_item);
    }

    return netlogic_items.filter((i) => !!i);
  }

  get combos() {
    const items = this.order?.cart?.catalog?.items || [];
    const netlogic_combos = [];

    for (const item of items) {
      const netlogic_combo = new NetLogicCombo(item);
      netlogic_combos.push(...netlogic_combo.export());
    }

    return netlogic_combos.filter((c) => !!c);
  }

  get subTotal() {
    const { order } = this;
    return order.cart.total;
  }

  get deliveryFee() {
    return +(this.order?.delivery?.total || 0).toFixed(4);
  }

  get totalPrice() {
    const { order } = this;
    return order.total;
  }

  get deliveryAddress() {
    return {};
  }

  get deliveryDateTime() {
    return null;
  }

  get preparationStartDateTime() {
    return this.createdAt;
  }

  get localizer() {
    return { id: 0 };
  }

  get preparationTimeInSeconds() {
    // WIP
    return 0;
  }

  get isTest() {
    return true;
  }

  get deliveryMethod() {
    // WIP
    return {};
  }

  get valid() {
    return !!this.export();
  }

  isNew() {
    return !this.checkSeq;
  }

  export() {
    try {
      return removeEmptyProps({
        id: this.id,
        reference: this.reference,
        shortReference: this.shortReference,
        type: this.type,
        revenueCenter: this.revenueCenter,
        employeeObjectNum: this.employeeObjectNum,
        createdAt: this.createdAt,
        scheduled: this.scheduled,
        discount: this.discount,
        discountCode: this.discountCode,
        discountInfo: this.discountInfo,
        checkNum: this.checkNum,
        checkSeq: this.checkSeq,
        checkId: this.checkId,
        tableNum: this.tableNum,
        merchant: this.merchant,
        benefits: this.benefits,
        payments: this.payments,
        customer: this.customer,
        items: this.items,
        combos: this.combos,
        subTotal: this.subTotal,
        deliveryFee: 0.0,
        totalPrice: this.totalPrice,
        deliveryAddress: this.deliveryAddress,
        deliveryDateTime: this.deliveryDateTime,
        preparationStartDateTime: this.preparationStartDateTime,
        localizer: this.localizer,
        preparationTimeInSeconds: this.preparationTimeInSeconds,
        isTest: this.isTest,
        deliveryMethod: this.deliveryMethod,
      });
    } catch (error) {
      console.log({ error });
    }
    return null;
  }

}
